import React, { useState, useEffect, useMemo, useRef, useContext } from 'react'
import InputModern from '../../ui/Input/InputModern'
import ContextMenu from './ContextMenu';
import { formatNumber } from 'utils/formatNumber';
import { calculateWordLength } from 'views/company_settings/components/CompanyProfile';
import { Button, Dialog, Notification, Tooltip, toast } from 'components/ui';
import { IoLockClosedOutline, IoLockOpenOutline } from 'react-icons/io5';
import { Editor, EditorState } from 'draft-js';
import 'react-quill/dist/quill.snow.css';
import isMac from 'utils/isMac';
import { isMobile } from 'utils/isMobile';
import { ModalContext } from 'contexts/ModalContext';
import ExploreMedia from 'views/media/ExploreMedia';
import { PriorityModalContext } from 'contexts/PriorityModalContext';
import ImageResize from 'quill-image-resize-module-react';
import ReactQuill, { Quill } from 'react-quill';
import { calculateTwitterChars } from 'utils/calculateTwitterChars';
import { HiOutlineX } from 'react-icons/hi';
import { HiOutlineCheck } from 'react-icons/hi2';
import { TbDots } from 'react-icons/tb';
import { BsThreeDots } from 'react-icons/bs';
import OptionsBar from './OptionsBar';
import CloseButton from 'components/ui/CloseButton';
import { ContextMenuContext } from 'contexts/ContextMenuContext';
import autosize from 'autosize';

Quill.register('modules/imageResize', ImageResize);

const MagicEditor = (props) => {

    const {
        value, 
        setValue, 
        className,
        footerClassName,
        minHeight = 100,
        network,
        mediaProductType = 'FEED',
        isHTML,
        isTitle = false,
        onCancel = undefined,
        onConfirm = undefined,
        post = undefined,
        showOptionsBar = true,
        placeholder = "",
        optionsBarClassName = "",
        ...rest 
    } = props;

    const ref = useRef();
    const { isOpen, openContextMenu, closeContextMenu } = useContext(ContextMenuContext);
    const [ isLocked, setIsLocked ] = useState(false);
    const [ isOpenedExploreAssetsDialog, setIsOpenedExploreAssetsDialog ] = useState(false);
    const { openModalPriority, closeModalPriority } = useContext(PriorityModalContext);

    function getSelection(element) {
        var start = element.selectionStart;
        var finish = element.selectionEnd;
        var sel = element.value.substring(start, finish);
        return sel;
    }

    function getSelectionStartEnd(element) {
        var start = element.selectionStart;
        var finish = element.selectionEnd;
        return { start: start, finish: finish };
    }

    const resize = () => {
        if (!(isHTML || (network == "medium" && isTitle == false))) {
            ref.current.style.height = 'inherit';
            ref.current.style.height = `${Math.max(ref.current.scrollHeight, minHeight)}px`;
        }
    }
    
    function createCopy(textArea) {
        var copy = document.createElement('div');
        copy.textContent = textArea.value + " ";
        var style = getComputedStyle(textArea);
        ['fontFamily', 'fontSize', 'fontWeight', 'wordWrap',  'whiteSpace', 'borderLeftWidth', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth',].forEach(function(key) {
            copy.style[key] = style[key];
        });
        // Overlay the copy exactly on the textarea (viewport coords) so the
        // measured caret range is already in viewport space — offsetLeft/Top
        // are relative to the nearest positioned ancestor and break inside
        // nested positioned containers (e.g. the post composer panel).
        var textAreaRect = textArea.getBoundingClientRect();
        copy.style.overflow = 'auto';
        copy.style.width = textArea.offsetWidth + 'px';
        copy.style.height = textArea.offsetHeight + 'px';
        copy.style.position = 'fixed';
        copy.style.left = textAreaRect.left + 'px';
        copy.style.top = textAreaRect.top + 'px';
        copy.style.visibility = 'hidden';
        document.body.appendChild(copy);
        return copy;
    }

    function getCaretPosition(textArea) {
        var start = textArea.selectionStart;
        var end = textArea.selectionEnd;
        var copy = createCopy(textArea);
        var range = document.createRange();
        range.setStart(copy.firstChild, start);
        range.setEnd(copy.firstChild, end);
        var selection = document.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        var rect = range.getBoundingClientRect();
        document.body.removeChild(copy);
        textArea.selectionStart = start;
        textArea.selectionEnd = end;
        textArea.focus();
        return {
            x: rect.left,
            y: rect.top + 28
        };
    }

    const computeMenuPosition = (e, source, el) => {
        // Right-click: anchor at the mouse — the most reliable, expected spot.
        if (source === 'mouse' && e && e.clientX != null && (e.clientX || e.clientY)) {
            return { x: e.clientX, y: e.clientY };
        }
        const rect = el?.getBoundingClientRect?.();
        // Cmd/Ctrl+E: anchor at the caret, but fall back when it degenerates to
        // 0,0 or lands outside the field (empty/collapsed selection in some browsers).
        if (source === 'caret' && el) {
            const caret = getCaretPosition(el);
            if (caret && (caret.x || caret.y) && (!rect ||
                (caret.x >= rect.left - 2 && caret.x <= rect.right + 2 && caret.y >= rect.top - 2 && caret.y <= rect.bottom + 48))) {
                return caret;
            }
        }
        // Fallback (bar trigger / empty field): just inside the editor's top-left.
        if (rect) return { x: rect.left + 12, y: rect.top + 28 };
        return { x: 120, y: 120 };
    }

    const handleOnContextMenu = (e, source = 'mouse') => {
        e.stopPropagation?.();
        e.preventDefault?.();

        if (isLocked) {
            toast.push(<Notification title="Unlock the editor to continue writing." type="warning" />);
            return;
        }

        if ((isHTML || (network == "medium" && isTitle == false))) {
            toast.push(<Notification title="AI options are not available for HTML content editor at the moment." type="warning" />);
            return;
        }

        const el = ref.current?.editingArea ? ref.current?.editingArea : ref.current;
        if (!el) return;

        openContextMenu({
            network,
            value,
            onChange: setValue,
            position: computeMenuPosition(e, source, el),
            fullText: el.value !== undefined ? el.value : value,
            selectionStartEnd: getSelectionStartEnd(el),
            selectedText: getSelection(el),
        });
    }

    // Entry point for the OptionsBar "AI Tools" button (mouse/keyboard-only users).
    const openAIToolsFromBar = () => {
        const el = ref.current?.editingArea ? ref.current?.editingArea : ref.current;
        // On phones the menu is a bottom sheet; focusing the field first would pop
        // the soft keyboard under the sheet. Desktop keeps focus for caret context.
        if (!isMobile()) el?.focus?.();
        handleOnContextMenu({ target: el }, 'bar');
    }

    const insertTextQuill = (position, value) => {
        ref.current.editor.insertText(position, value);
    }

    const imageHandler = () => {
        openModalPriority(
            <ExploreMedia isSelectable={true} handleOnSelect={imageSelected} showMyAssets={true} allowedTypes={["image"]} />
        , 1200, true)
    }

    const imageSelected = (url) => {
        const range = ref.current.getEditorSelection();  
        ref.current.editor.insertEmbed(range?.index ? range?.index : 0, 'image', url);
        closeModalPriority();
    }

    const module =  React.useMemo(() => { 
        return {
            toolbar: {
                container: [
                    [{ 'header': '1'}, {'header': '2'}],
                    ['bold', 'italic', 'blockquote'],
                    // [{'list': 'ordered'}, {'list': 'bullet'}, 
                    ['link', 'image'],
                    ['clean']
                ],
                handlers: {  
                    image: () => imageHandler()  
                },   
            },
            imageResize: {
                parchment: Quill.import('parchment'),
                modules: ['Resize']
            },
            clipboard: {
                matchVisual: false,
            }   
        }
    },[]);

    useEffect(() => {
        if (!(isHTML || (network == "medium" && isTitle == false))) {
            if (ref.current) autosize(ref.current);
        }
    }, [ref.current]);

    // Re-fit the textarea after programmatic changes too — AI generations set
    // `value` without firing input events, so autosize wouldn't catch them.
    useEffect(() => {
        if (!(isHTML || (network == "medium" && isTitle == false)) && ref.current) {
            autosize.update(ref.current);
        }
    }, [value]);

    return (
        <> 
            {!!showOptionsBar && <OptionsBar
                value={value}
                setValue={setValue}
                isHTML={isHTML}
                isTitle={isTitle}
                network={network}
                onCancel={onCancel}
                onConfirm={onConfirm}
                post={post}
                className={optionsBarClassName}
                onOpenAITools={(isHTML || (network == "medium" && isTitle == false)) ? undefined : openAIToolsFromBar}
            />}
            {!(isHTML || (network == "medium" && isTitle == false)) ?
                <textarea
                    ref={ref}
                    onClick={(e) => { closeContextMenu(); }}
                    onContextMenu={(e) => { handleOnContextMenu(e, 'mouse'); }}
                    onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === 'KeyE') {
                            e.preventDefault();
                            if (isOpen) {
                                closeContextMenu();
                            } else {
                                handleOnContextMenu(e, 'caret');
                            }
                        }
                        if (e.key == "Escape") {
                            closeContextMenu();
                        }
                    }}
                    className={`!outline-none followr-magic-editor magic-editor p-4 lg:p-8 !overflow-y-auto text-base ${className}`}
                    value={value ? value : ""}
                    onChange={(e) => { setValue(e.target.value); }}
                    readOnly={isLocked}
                    placeholder={placeholder || `Right-click or press "${isMac() ? `⌘` : `Ctrl`} + Shift + E" key to open AI options`}
                    {...rest}
                />
                :
                <>
                    <ReactQuill 
                        ref={ref}
                        theme={"snow"}
                        onClick={(e) => { closeContextMenu(); }}
                        onContextMenu={(e) => { handleOnContextMenu(e, 'mouse'); }}
                        onKeyDown={(e) => {
                            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === 'KeyE') {
                                e.preventDefault();
                                if (isOpen) {
                                    closeContextMenu();
                                } else {
                                    handleOnContextMenu(e, 'caret');
                                }
                            }
                            if (e.key == "Escape") {
                                closeContextMenu();
                            }
                        }}
                        className={`${className} followr-magic-editor`}
                        onChange={(html) => { setValue(html); }}
                        value={value}
                        modules={module}
                        formats={[
                            'header', 'font', 'size',
                            'bold', 'italic', 'underline', 'strike', 'blockquote',
                            'list', 'bullet', 'indent',
                            'link', 'image', 'video', 'width', 'height'
                        ]}
                        bounds={'.app'}
                        readOnly={isLocked}
                        // placeholder={`Right-click or press "${isMac() ? `⌘` : `Ctrl`} + E" key to open AI options`}
                        {...rest}
                    />
                </>
            }
        </>
    );
}


export default MagicEditor
